import { clsx } from 'clsx'
import {
  forwardRef,
  type PointerEvent as RPointerEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { sampleColors } from '@/lib/canvasRegistry'
import { classifyPdfFont } from '@/lib/fonts'
import {
  annBBox,
  clamp,
  clientToPage,
  hitTest,
  normRect,
  transformAnn,
  translateAnn,
  unionRects,
} from '@/lib/geometry'
import { uid } from '@/lib/id'
import { getSource } from '@/lib/pdf/sources'
import { editableLines, getPageText, type TextRun } from '@/lib/pdf/text'
import { findAnnotation, useDoc } from '@/store/documentStore'
import { useUi } from '@/store/uiStore'
import type { Annotation, DocSnapshot, PageRef, Point, Rect, ShapeAnn, TextEditAnn, Tool } from '@/types'
import { FreeTextView, isMarkup, MarkupShape, NoteIcon, SvgShape, TextEditView } from './AnnotationViews'
import { FreeTextEditor, NotePopup, TextEditEditor } from './Editors'

export interface OverlayHandle {
  startArea(e: PointerEvent): void
}

interface Props {
  page: PageRef
  scale: number
  pageEl: () => HTMLElement | null
}

const DRAW_TOOLS: Tool[] = [
  'pen',
  'marker',
  'rect',
  'ellipse',
  'line',
  'arrow',
  'text',
  'note',
  'eraser',
  'editText',
]
const EMPTY: Annotation[] = []

type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'p1' | 'p2'

export const PageOverlay = forwardRef<OverlayHandle, Props>(function PageOverlay(
  { page, scale, pageEl },
  ref,
) {
  const annotations = useDoc((s) => s.annotations[page.id] ?? EMPTY)
  const tool = useUi((s) => s.tool)
  const selection = useUi((s) => s.selection)
  const editingId = useUi((s) => s.editingId)
  const stylusDetected = useUi((s) => s.stylusDetected)
  const [draft, setDraft] = useState<Annotation | null>(null)
  const [draftEdit, setDraftEdit] = useState<TextEditAnn | null>(null)
  const [lines, setLines] = useState<TextRun[]>([])
  const root = useRef<HTMLDivElement>(null)

  const toPage = useCallback(
    (e: { clientX: number; clientY: number }): Point => {
      const el = pageEl()
      if (!el) return { x: 0, y: 0 }
      return clientToPage(
        e.clientX,
        e.clientY,
        el.getBoundingClientRect(),
        page.rotation,
        scale,
        page.width,
        page.height,
      )
    },
    [pageEl, page.rotation, page.width, page.height, scale],
  )

  const clampPt = (p: Point): Point => ({ x: clamp(p.x, 0, page.width), y: clamp(p.y, 0, page.height) })

  // ----- generic window-level drag helper -----
  const drag = useCallback(
    (
      e: PointerEvent | RPointerEvent,
      onMove: (p: Point, ev: PointerEvent) => void,
      onUp: (p: Point, moved: boolean, ev: PointerEvent) => void,
      onCancel: () => void = () => setDraft(null),
    ) => {
      const sx = e.clientX
      const sy = e.clientY
      let moved = false
      const id = e.pointerId
      // a second finger means the user is pinching/panning: abandon the stroke
      const second = (ev: PointerEvent) => {
        if (ev.pointerId === id || ev.pointerType !== 'touch') return
        cleanup()
        onCancel()
      }
      const cleanup = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        window.removeEventListener('pointercancel', up)
        window.removeEventListener('pointerdown', second, true)
      }
      window.addEventListener('pointerdown', second, true)
      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== id) return
        if (!moved && Math.hypot(ev.clientX - sx, ev.clientY - sy) < 3) return
        moved = true
        const evs = 'getCoalescedEvents' in ev ? ev.getCoalescedEvents() : []
        if (evs.length > 1) for (const c of evs) onMove(toPage(c), c)
        else onMove(toPage(ev), ev)
      }
      const up = (ev: PointerEvent) => {
        if (ev.pointerId !== id) return
        cleanup()
        if (ev.type === 'pointercancel' && !moved) return onCancel()
        onUp(toPage(ev), moved, ev)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
      window.addEventListener('pointercancel', up)
    },
    [toPage],
  )

  const liveReplace = (s: DocSnapshot, updated: Map<string, Annotation>): DocSnapshot => ({
    ...s,
    annotations: {
      ...s.annotations,
      [page.id]: (s.annotations[page.id] ?? []).map((x) => updated.get(x.id) ?? x),
    },
  })

  // ----- move / resize selected annotations -----
  const startTransform = (
    e: RPointerEvent | PointerEvent,
    mode: 'move' | Handle,
    ids: string[],
    tapped?: Annotation,
  ) => {
    const doc = useDoc.getState()
    const before = doc.snapshot()
    const originals = ids
      .map((id) => findAnnotation(before, id))
      .filter((a): a is Annotation => a?.pageId === page.id)
    if (!originals.length) return
    const box = unionRects(originals.map(annBBox))
    const start = toPage(e)
    const keepAspect = originals.length === 1 && originals[0].type === 'image'
    drag(
      e,
      (p, ev) => {
        let dx = p.x - start.x
        let dy = p.y - start.y
        const updated = new Map<string, Annotation>()
        if (mode === 'move') {
          dx = clamp(dx, -box.x, page.width - box.x - box.w)
          dy = clamp(dy, -box.y, page.height - box.y - box.h)
          for (const o of originals) updated.set(o.id, translateAnn(o, dx, dy))
        } else if (mode === 'p1' || mode === 'p2') {
          const o = originals[0]
          if (o.type === 'line' || o.type === 'arrow') {
            const q = clampPt(p)
            updated.set(o.id, mode === 'p1' ? { ...o, x1: q.x, y1: q.y } : { ...o, x2: q.x, y2: q.y })
          }
        } else {
          let { x, y, w, h } = box
          const r = x + w
          const b = y + h
          let nx = x
          let ny = y
          let nr = r
          let nb = b
          if (mode.includes('w')) nx = Math.min(r - 4, x + dx)
          if (mode.includes('e')) nr = Math.max(x + 4, r + dx)
          if (mode.includes('n')) ny = Math.min(b - 4, y + dy)
          if (mode.includes('s')) nb = Math.max(y + 4, b + dy)
          w = nr - nx
          h = nb - ny
          if ((keepAspect || ev.shiftKey) && mode.length === 2 && box.w && box.h) {
            const k = Math.max(w / box.w, h / box.h)
            w = box.w * k
            h = box.h * k
            if (mode.includes('w')) nx = r - w
            if (mode.includes('n')) ny = b - h
          }
          const to: Rect = { x: nx, y: ny, w, h }
          for (const o of originals) updated.set(o.id, transformAnn(o, box, to))
        }
        useDoc.getState().live((s) => liveReplace(s, updated))
      },
      (_p, moved) => {
        if (moved) useDoc.getState().checkpoint(before)
        else if (
          tapped &&
          (tapped.type === 'text' || tapped.type === 'note') &&
          useUi.getState().selection.length === 1
        ) {
          // a tap on an already-selected text box starts editing
          if (wasSelected.current) useUi.getState().setEditing(tapped.id)
        }
      },
    )
  }

  const wasSelected = useRef(false)

  const onAnnDown = (e: RPointerEvent, a: Annotation) => {
    const ui = useUi.getState()
    if (e.button > 0) return
    if (ui.tool === 'editText' && a.type !== 'textEdit' && a.type !== 'text') return
    e.stopPropagation()
    e.preventDefault()
    if (ui.editingId === a.id) return
    if (a.type === 'textEdit') {
      ui.select([a.id])
      ui.setEditing(a.id)
      return
    }
    wasSelected.current = ui.selection.includes(a.id)
    const ids = ui.selection.includes(a.id)
      ? ui.selection
      : e.shiftKey || e.metaKey || e.ctrlKey
        ? [...ui.selection, a.id]
        : [a.id]
    ui.select(ids)
    if (ui.editingId) ui.setEditing(null)
    if (a.type === 'note') {
      wasSelected.current = true
    }
    startTransform(e, 'move', ids, a)
  }

  // ----- creation tools -----
  const onRootDown = (e: RPointerEvent) => {
    const ui = useUi.getState()
    if (e.button > 0) return
    if (e.pointerType === 'pen' && !ui.stylusDetected) ui.setStylusDetected(true)
    if (
      ui.stylusDetected &&
      e.pointerType === 'touch' &&
      (tool === 'pen' || tool === 'marker' || tool === 'eraser')
    )
      return
    if (ui.editingId) {
      ui.setEditing(null)
      ui.select([])
      if (tool === 'select' || tool === 'editText') return
    }
    if (draftEdit) {
      commitDraftEdit()
      return
    }
    const style = ui.styleFor(tool)
    const p = clampPt(toPage(e))
    const base = {
      pageId: page.id,
      color: style.color,
      opacity: style.opacity,
      createdAt: Date.now(),
      author: ui.author || undefined,
    }
    e.preventDefault()

    switch (tool) {
      case 'pen':
      case 'marker': {
        const pressure = e.pointerType === 'pen' ? e.pressure || 0.5 : 0.5
        let cur: Annotation = {
          ...base,
          id: uid('ann'),
          type: 'ink',
          points: [[p.x, p.y, pressure]],
          width: style.width,
          marker: tool === 'marker' || undefined,
        }
        setDraft(cur)
        drag(
          e,
          (q, ev) => {
            if (cur.type !== 'ink') return
            const pr = ev.pointerType === 'pen' ? ev.pressure || 0.5 : 0.5
            cur = { ...cur, points: [...cur.points, [q.x, q.y, pr]] }
            setDraft(cur)
          },
          () => {
            setDraft(null)
            useDoc.getState().addAnnotation(cur)
          },
        )
        break
      }
      case 'rect':
      case 'ellipse':
      case 'line':
      case 'arrow': {
        const make = (q: Point, shift: boolean): Annotation => {
          if (tool === 'line' || tool === 'arrow') {
            let x2 = q.x
            let y2 = q.y
            if (shift) {
              const ang = Math.round(Math.atan2(q.y - p.y, q.x - p.x) / (Math.PI / 4)) * (Math.PI / 4)
              const len = Math.hypot(q.x - p.x, q.y - p.y)
              x2 = p.x + len * Math.cos(ang)
              y2 = p.y + len * Math.sin(ang)
            }
            return { ...base, id: 'draft', type: tool, x1: p.x, y1: p.y, x2, y2, strokeWidth: style.width }
          }
          let r = normRect(p.x, p.y, q.x, q.y)
          if (shift) {
            const s = Math.max(r.w, r.h)
            r = normRect(p.x, p.y, p.x + Math.sign(q.x - p.x || 1) * s, p.y + Math.sign(q.y - p.y || 1) * s)
          }
          return {
            ...base,
            id: 'draft',
            type: tool as 'rect' | 'ellipse',
            ...r,
            strokeWidth: style.width,
            fill: style.fill,
          }
        }
        drag(
          e,
          (q, ev) => setDraft(make(clampPt(q), ev.shiftKey)),
          (q, moved, ev) => {
            setDraft(null)
            let a = make(clampPt(q), ev.shiftKey)
            if (!moved) {
              // a simple tap drops a default-sized shape
              const s = 72
              a =
                a.type === 'line' || a.type === 'arrow'
                  ? { ...a, x2: Math.min(page.width, p.x + s * 1.4), y2: p.y }
                  : { ...(a as ShapeAnn), x: p.x - s / 2, y: p.y - s / 3, w: s, h: (s * 2) / 3 }
            }
            const id = uid('ann')
            useDoc.getState().addAnnotation({ ...a, id })
            useUi.getState().select([id])
          },
        )
        break
      }
      case 'text':
      case 'editText': {
        drag(
          e,
          (q) =>
            setDraft({
              ...base,
              id: 'draft',
              type: 'rect',
              ...normRect(p.x, p.y, q.x, q.y),
              strokeWidth: 1 / scale,
              fill: null,
              color: '#6366f1',
              opacity: 1,
            }),
          (q, moved) => {
            setDraft(null)
            const ts = ui.styleFor('text')
            const r = moved
              ? normRect(p.x, p.y, q.x, q.y)
              : { x: p.x, y: p.y - ts.fontSize * 0.7, w: 0, h: 0 }
            const w = Math.max(r.w, Math.min(220, page.width - r.x - 4))
            const h = Math.max(r.h, ts.fontSize * 1.2 + 4)
            const id = uid('ann')
            useDoc.getState().addAnnotation({
              ...base,
              id,
              type: 'text',
              x: Math.min(r.x, page.width - w),
              y: Math.min(r.y, page.height - h),
              w,
              h,
              text: '',
              color: ts.color,
              opacity: ts.opacity,
              fontSize: ts.fontSize,
              fontFamily: ts.fontFamily,
              bold: ts.bold,
              italic: ts.italic,
              background: ts.fill,
            })
            if (tool === 'text') useUi.getState().setTool('select')
            useUi.getState().select([id])
            useUi.getState().setEditing(id)
          },
        )
        break
      }
      case 'note': {
        const id = uid('ann')
        useDoc.getState().addAnnotation({
          ...base,
          id,
          type: 'note',
          x: Math.min(p.x, page.width - 22),
          y: Math.min(p.y, page.height - 22),
          text: '',
        })
        useUi.getState().setTool('select')
        useUi.getState().select([id])
        useUi.getState().setEditing(id)
        break
      }
      case 'eraser': {
        const before = useDoc.getState().snapshot()
        let erased = false
        const eraseAt = (q: Point) => {
          const list = useDoc.getState().annotations[page.id] ?? []
          const tol = 6 / scale
          const hit = list.filter((a) => hitTest(a, q, tol))
          if (!hit.length) return
          erased = true
          const ids = new Set(hit.map((a) => a.id))
          useDoc.getState().live((s) => ({
            ...s,
            annotations: {
              ...s.annotations,
              [page.id]: (s.annotations[page.id] ?? []).filter((a) => !ids.has(a.id)),
            },
          }))
        }
        eraseAt(p)
        drag(
          e,
          (q) => eraseAt(q),
          () => {
            if (erased) useDoc.getState().checkpoint(before)
          },
        )
        break
      }
    }
  }

  // area highlight/underline/strikeout (drag on non-text areas — works on scanned pages too)
  useImperativeHandle(ref, () => ({
    startArea(e: PointerEvent) {
      const ui = useUi.getState()
      const t = ui.tool
      if (t !== 'highlight' && t !== 'underline' && t !== 'strikeout') return
      const style = ui.styleFor(t)
      const p = clampPt(toPage(e))
      e.preventDefault()
      const make = (q: Point): Annotation => ({
        id: 'draft',
        pageId: page.id,
        type: t,
        rects: [normRect(p.x, p.y, q.x, q.y)],
        color: style.color,
        opacity: style.opacity,
        createdAt: Date.now(),
      })
      drag(
        e,
        (q) => setDraft(make(clampPt(q))),
        (q, moved) => {
          setDraft(null)
          if (!moved) return
          const a = make(clampPt(q))
          if (annBBox(a).w < 3 || annBBox(a).h < 3) return
          useDoc.getState().addAnnotation({ ...a, id: uid('ann'), author: ui.author || undefined })
        },
      )
    },
  }))

  // ----- edit existing text -----
  useEffect(() => {
    if (tool !== 'editText' || !page.srcId) {
      setLines([])
      return
    }
    let alive = true
    getPageText(page).then((pt) => alive && setLines(editableLines(pt)))
    return () => {
      alive = false
    }
  }, [tool, page])

  const editedLines = useMemo(() => {
    const edits = annotations.filter((a): a is TextEditAnn => a.type === 'textEdit')
    return lines.filter(
      (l) =>
        !edits.some(
          (e) =>
            Math.abs(e.y + e.baseline - l.baseline) < l.fontSize * 0.4 && e.x < l.x + l.w && e.x + e.w > l.x,
        ),
    )
  }, [lines, annotations])

  const beginLineEdit = async (e: RPointerEvent, line: TextRun) => {
    e.stopPropagation()
    e.preventDefault()
    if (draftEdit) commitDraftEdit()
    useUi.getState().setEditing(null)
    const { bg, fg } = sampleColors(page, line)
    let style = classifyPdfFont(line.generic, undefined)
    try {
      const proxy = await getSource(page.srcId)?.pdf.getPage(page.srcIndex + 1)
      if (proxy?.commonObjs.has(line.fontName)) {
        const f = proxy.commonObjs.get(line.fontName) as {
          name?: string
          bold?: boolean
          italic?: boolean
          black?: boolean
        }
        const c = classifyPdfFont(line.generic, f.name)
        style = { ...c, bold: c.bold || Boolean(f.bold || f.black), italic: c.italic || Boolean(f.italic) }
      }
    } catch {
      /* font details are optional */
    }
    setDraftEdit({
      id: uid('ann'),
      pageId: page.id,
      type: 'textEdit',
      x: line.x,
      y: line.y,
      w: line.w,
      h: line.h,
      baseline: line.baseline - line.y,
      original: line.str,
      text: line.str,
      fontSize: Math.round(line.fontSize * 10) / 10,
      ...style,
      color: fg,
      cover: bg,
      opacity: 1,
      createdAt: Date.now(),
      author: useUi.getState().author || undefined,
    })
  }

  const draftRef = useRef(draftEdit)
  draftRef.current = draftEdit
  const commitDraftEdit = useCallback(() => {
    const d = draftRef.current
    setDraftEdit(null)
    if (d && d.text !== d.original) useDoc.getState().addAnnotation(d)
  }, [])

  useEffect(() => {
    if (tool !== 'editText' && draftRef.current) commitDraftEdit()
  }, [tool, commitDraftEdit])

  // ----- render -----
  const interactiveAnns = tool === 'select' || tool === 'editText'
  const capture = DRAW_TOOLS.includes(tool)
  const selectedHere = annotations.filter((a) => selection.includes(a.id))
  const markups = annotations.filter(isMarkup)
  const draftMarkup = draft && isMarkup(draft) ? draft : null

  return (
    <>
      {/* Replaced text sits directly on the page, under highlights and drawings. */}
      <div
        className="edit-layer pointer-events-none absolute top-0 left-0 z-[3] origin-top-left"
        style={{ width: page.width, height: page.height, transform: `scale(${scale})` }}
      >
        {annotations.map((a) =>
          a.type === 'textEdit' && a.id !== editingId ? (
            <TextEditView key={a.id} a={a} interactive={false} />
          ) : null,
        )}
      </div>
      {/* Highlights live in their own multiply-blended layer (a sibling of the canvas), so the
          text underneath stays crisp and readable — just like a real highlighter. */}
      <svg
        className="markup-layer pointer-events-none absolute top-0 left-0 z-[3] origin-top-left overflow-visible"
        width={page.width}
        height={page.height}
        style={{ transform: `scale(${scale})`, mixBlendMode: 'multiply' }}
        aria-hidden
      >
        {markups.map((a) => (
          <MarkupShape key={a.id} a={a} />
        ))}
        {draftMarkup && <MarkupShape a={draftMarkup} />}
      </svg>
      <div
        ref={root}
        className={clsx('ann-layer absolute top-0 left-0 origin-top-left', capture && 'cursor-crosshair')}
        style={{
          width: page.width,
          height: page.height,
          transform: `scale(${scale})`,
          pointerEvents: capture ? 'auto' : 'none',
          touchAction: capture && !stylusDetected ? 'none' : 'pan-x pan-y',
          cursor:
            tool === 'eraser' ? ERASER_CURSOR : tool === 'text' || tool === 'editText' ? 'text' : undefined,
        }}
        onPointerDown={capture ? onRootDown : undefined}
      >
        <svg
          className="pointer-events-none absolute inset-0 overflow-visible"
          width={page.width}
          height={page.height}
          aria-hidden
        >
          {annotations.map((a) => (
            <SvgShape
              key={a.id}
              a={a}
              interactive={interactiveAnns && tool === 'select'}
              onDown={onAnnDown}
            />
          ))}
          {draft && !draftMarkup && <SvgShape a={draft} interactive={false} />}
        </svg>

        {annotations.map((a) => {
          if (a.type === 'text')
            return a.id === editingId ? (
              <FreeTextEditor key={a.id} a={a} scale={scale} />
            ) : (
              <FreeTextView key={a.id} a={a} interactive={interactiveAnns} onDown={onAnnDown} />
            )
          if (a.type === 'textEdit')
            return a.id === editingId ? (
              <TextEditEditor key={a.id} a={a} onDone={() => useUi.getState().setEditing(null)} />
            ) : interactiveAnns ? (
              <div
                key={a.id}
                data-ann={a.id}
                title="Click to edit"
                onPointerDown={(e) => onAnnDown(e, a)}
                className="absolute rounded-[1px] hover:outline hover:outline-1 hover:outline-accent"
                style={{
                  left: a.x,
                  top: a.y,
                  width: a.w,
                  height: a.h,
                  pointerEvents: 'auto',
                  cursor: 'text',
                }}
              />
            ) : null
          if (a.type === 'note')
            return (
              <NoteIcon
                key={a.id}
                a={a}
                interactive={tool === 'select'}
                onDown={onAnnDown}
                selected={selection.includes(a.id)}
              />
            )
          return null
        })}

        {tool === 'editText' &&
          editedLines.map((l, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Edit text: ${l.str}`}
              onPointerDown={(e) => beginLineEdit(e, l)}
              className="absolute rounded-[1px] outline outline-1 outline-accent/35 transition-colors hover:bg-accent/10 hover:outline-accent"
              style={{
                left: l.x - 1,
                top: l.y - 1,
                width: l.w + 2,
                height: l.h + 2,
                pointerEvents: 'auto',
                cursor: 'text',
              }}
            />
          ))}

        {draftEdit && (
          <TextEditEditor
            a={draftEdit}
            onChange={(text) => setDraftEdit((d) => (d ? { ...d, text } : d))}
            onStyle={(patch) => setDraftEdit((d) => (d ? { ...d, ...patch } : d))}
            onDone={commitDraftEdit}
            onCancel={() => setDraftEdit(null)}
          />
        )}

        {tool === 'select' && selectedHere.length > 0 && !editingId && (
          <SelectionFrame
            anns={selectedHere}
            scale={scale}
            onHandle={(e, h) =>
              startTransform(
                e,
                h,
                selectedHere.map((a) => a.id),
              )
            }
          />
        )}

        {annotations.map((a) =>
          a.type === 'note' && selection.includes(a.id) && tool === 'select' ? (
            <NotePopup key={`p-${a.id}`} a={a} scale={scale} page={page} autoFocus={editingId === a.id} />
          ) : null,
        )}
      </div>
    </>
  )
})

const ERASER_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"><circle cx="10" cy="10" r="7" fill="white" fill-opacity=".6" stroke="#111" stroke-width="1.5"/></svg>',
)}") 10 10, crosshair`

function SelectionFrame({
  anns,
  scale,
  onHandle,
}: {
  anns: Annotation[]
  scale: number
  onHandle: (e: RPointerEvent, h: Handle) => void
}) {
  const box = unionRects(anns.map(annBBox))
  const single = anns.length === 1 ? anns[0] : null
  const s = 1 / scale
  const pad = 3 * s
  const hs = 10 * s
  const touch = 26 * s
  const isLine = single && (single.type === 'line' || single.type === 'arrow')
  const resizable =
    !single || !['note', 'textEdit', 'highlight', 'underline', 'strikeout'].includes(single.type)
  const handles: [Handle, number, number, string][] = isLine
    ? [
        ['p1', (single as { x1: number }).x1, (single as { y1: number }).y1, 'move'],
        ['p2', (single as { x2: number }).x2, (single as { y2: number }).y2, 'move'],
      ]
    : resizable
      ? [
          ['nw', box.x - pad, box.y - pad, 'nwse-resize'],
          ['n', box.x + box.w / 2, box.y - pad, 'ns-resize'],
          ['ne', box.x + box.w + pad, box.y - pad, 'nesw-resize'],
          ['e', box.x + box.w + pad, box.y + box.h / 2, 'ew-resize'],
          ['se', box.x + box.w + pad, box.y + box.h + pad, 'nwse-resize'],
          ['s', box.x + box.w / 2, box.y + box.h + pad, 'ns-resize'],
          ['sw', box.x - pad, box.y + box.h + pad, 'nesw-resize'],
          ['w', box.x - pad, box.y + box.h / 2, 'ew-resize'],
        ]
      : []
  return (
    <svg
      className="pointer-events-none absolute inset-0 overflow-visible"
      width="100%"
      height="100%"
      aria-hidden
    >
      {!isLine && (
        <rect
          x={box.x - pad}
          y={box.y - pad}
          width={box.w + pad * 2}
          height={box.h + pad * 2}
          fill="none"
          stroke="var(--accent)"
          strokeWidth={1.25 * s}
          strokeDasharray={`${4 * s} ${3 * s}`}
          rx={2 * s}
        />
      )}
      {handles.map(([h, x, y, cursor]) => (
        <g
          key={h}
          style={{ pointerEvents: 'auto', cursor }}
          onPointerDown={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onHandle(e, h)
          }}
        >
          <rect x={x - touch / 2} y={y - touch / 2} width={touch} height={touch} fill="transparent" />
          <rect
            x={x - hs / 2}
            y={y - hs / 2}
            width={hs}
            height={hs}
            rx={h.startsWith('p') ? hs / 2 : 2.5 * s}
            fill="white"
            stroke="var(--accent)"
            strokeWidth={1.5 * s}
          />
        </g>
      ))}
    </svg>
  )
}
